import { useId, useState } from 'react';
import { CloseRounded } from '@mui/icons-material';
import { Drawer } from '@mui/material';

import { displaySpeakerName, UNIDENTIFIED_SPEAKER_LABEL } from './speakerHelper';
import './PreviewQueueDrawer.css';

const validRows = (rows) => (Array.isArray(rows)
    ? rows.filter((row) => row?.preview?.id)
    : []);

const speakerInitials = (name) => {
    if (name === UNIDENTIFIED_SPEAKER_LABEL) return '?';
    const words = name.trim().split(/\s+/);
    return words.length > 1
        ? `${Array.from(words[0])[0]}${Array.from(words[words.length - 1])[0]}`.toUpperCase()
        : Array.from(name).slice(0, 2).join('').toUpperCase();
};

const QueueSpeakerImage = ({ speaker, name }) => {
    const [failedUrl, setFailedUrl] = useState(null);
    const imageUrl = name !== UNIDENTIFIED_SPEAKER_LABEL
        && typeof speaker?.imageURL === 'string'
        && /^https?:\/\//iu.test(speaker.imageURL)
        ? speaker.imageURL : '';

    return (
        <span className='previewQueueDrawerSpeakerImage' aria-hidden='true'>
            {imageUrl && imageUrl !== failedUrl ? (
                <img
                    src={imageUrl}
                    alt=''
                    loading='lazy'
                    onError={() => setFailedUrl(imageUrl)}
                />
            ) : speakerInitials(name)}
        </span>
    );
};

const QueueRow = ({ row, isCurrent = false, onSelectPreview }) => {
    const { preview } = row;
    const speaker = preview.person;
    const name = displaySpeakerName({
        id: speaker?.id,
        name: typeof speaker?.name === 'string' ? speaker.name : '',
    });
    const title = typeof preview.title === 'string' && preview.title.trim()
        ? preview.title.trim() : 'Untitled preview';
    const topic = typeof row.topic === 'string' ? row.topic.trim() : '';
    const label = `${title}, ${name}${topic ? `, ${topic}` : ''}`;

    return (
        <button
            type='button'
            className={[
                'previewQueueDrawerRow',
                isCurrent ? 'previewQueueDrawerRowCurrent' : '',
            ].filter(Boolean).join(' ')}
            aria-current={isCurrent ? 'true' : undefined}
            aria-label={isCurrent ? `${label}, current preview` : `Play ${label}`}
            onClick={() => onSelectPreview(preview.id)}
        >
            <QueueSpeakerImage speaker={speaker} name={name} />
            <span className='previewQueueDrawerRowCopy'>
                <span className='previewQueueDrawerSpeakerName'>{name}</span>
                <span className='previewQueueDrawerPreviewTitle'>{title}</span>
                {topic && <span className='previewQueueDrawerTopic'>{topic}</span>}
            </span>
        </button>
    );
};

const QueueRows = ({ rows, onSelectPreview }) => (
    <ul className='previewQueueDrawerRows'>
        {rows.map((row, index) => (
            <li key={`${row.preview.id}-${row.index ?? index}`}>
                <QueueRow row={row} onSelectPreview={onSelectPreview} />
            </li>
        ))}
    </ul>
);

const PreviewQueueDrawer = ({
    open,
    onClose,
    onTransitionExited,
    title,
    earlier = [],
    current = null,
    upcomingGroups = [],
    onSelectPreview,
}) => {
    const headingId = useId();
    const sourceId = useId();
    const sourceTitle = typeof title === 'string' ? title.trim() : '';
    const earlierRows = validRows(earlier);
    const currentRow = current?.preview?.id ? current : null;
    const groups = (Array.isArray(upcomingGroups) ? upcomingGroups : [])
        .map((group) => ({ ...group, rows: validRows(group?.rows) }))
        .filter((group) => group.rows.length);

    return (
        <Drawer
            anchor='bottom'
            open={open}
            onClose={onClose}
            ModalProps={{
                keepMounted: false,
                // Transfer focus in onTransitionExited before MUI marks the
                // closing modal aria-hidden; its focused row must stay visible.
                closeAfterTransition: true,
                // The parent may have paged to another preview before exit.
                // Restoring the old trigger here would scroll that card back in.
                disableRestoreFocus: true,
                onTransitionExited,
            }}
            PaperProps={{
                role: 'dialog',
                'aria-modal': true,
                'aria-labelledby': headingId,
                'aria-describedby': sourceTitle ? sourceId : undefined,
                sx: {
                    borderRadius: '18px 18px 0px 0px',
                    maxHeight: 'calc(var(--app-drawer-height, 100vh) * 0.9)',
                    overflow: 'hidden',
                },
            }}
        >
            <div className='previewQueueDrawer appDrawerSurface'>
                <div className='previewPreferenceDragIndicator' />
                <div className='previewQueueDrawerHeader'>
                    <div className='previewQueueDrawerHeading'>
                        <h2 id={headingId}>Queue</h2>
                        {sourceTitle && <p id={sourceId}>{sourceTitle}</p>}
                    </div>
                    <button
                        type='button'
                        className='previewQueueDrawerCloseButton'
                        aria-label='Close queue'
                        onClick={onClose}
                    >
                        <CloseRounded style={{ fontSize: '20px' }} aria-hidden='true' />
                    </button>
                </div>
                <div className='previewQueueDrawerContent'>
                    {earlierRows.length > 0 && (
                        <details className='previewQueueDrawerEarlier'>
                            <summary>Earlier · {earlierRows.length}</summary>
                            <QueueRows rows={earlierRows} onSelectPreview={onSelectPreview} />
                        </details>
                    )}
                    {currentRow && (
                        <QueueRow row={currentRow} isCurrent onSelectPreview={onSelectPreview} />
                    )}
                    {groups.map((group, index) => (
                        <section className='previewQueueDrawerGroup' key={group.id || index}>
                            <h3>{group.label || 'Up next'}</h3>
                            <QueueRows rows={group.rows} onSelectPreview={onSelectPreview} />
                        </section>
                    ))}
                </div>
            </div>
        </Drawer>
    );
};

export default PreviewQueueDrawer;
